import { KEYCODE, BREAKPOINT } from '@/constants';

/**
 * Class definition for `wp-header` element
 *
 * @class WPHeader
 * @extends {HTMLElement}
 */
class WPHeader extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._nav = this.querySelector('wp-nav');
    this._search = this.querySelector('wp-search');
    this._searchResults = document.querySelector('wp-search-results');
    this._lastFocus = null;
    this._scrolling = false;
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-header';
  }

  connectedCallback() {
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'banner');
    }

    this._bindHandlers();
    this._addEventListeners();
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._handleKeyDown, false);
    window.removeEventListener('scroll', this._handleScroll, false);
  }

  _addEventListeners() {
    this.addEventListener(
      'menuitemchanged',
      this._handleMenuItemChanged,
      false,
    );
    this.addEventListener('menutoggle', this._handleMenuToggle, false);
    this.addEventListener('menuopened', this._handleMenuOpened, false);
    this.addEventListener('menuclosed', this._handleMenuClosed, false);
    window.addEventListener('keydown', this._handleKeyDown, false);
    window.addEventListener('scroll', this._handleScroll, false);
  }

  _bindHandlers() {
    this._autoHide = this._autoHide.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
  }

  _handleMenuItemChanged() {
    // Close search results
    this._searchResults.$emit('searchresultsclose');
  }

  _handleMenuToggle(evt) {
    if (this._nav.opened) {
      this._handleMenuClosed();
    } else {
      this._handleMenuOpened(evt);
    }
  }

  _handleMenuOpened(evt) {
    // Save last focus
    this._lastFocus = evt.target;
    this._lastFocus.setAttribute('aria-expanded', 'true');

    // Open menu
    this._nav.opened = true;

    // Focus on first tabbable element
    // this._nav.querySelector('a').focus();
  }

  _handleMenuClosed() {
    this._nav.opened = false;
    // this._lastFocus.focus();
    this._lastFocus.setAttribute('aria-expanded', 'false');
  }

  _handleKeyDown(evt) {
    const keyCode = evt.which;

    // If menu open and focus outside, close menu
    if (!this._nav.contains(evt.target) && this._nav.opened) {
      this._handleMenuClosed();
      return;
    }

    // If menu open and ESC key, close memenu
    if (keyCode === KEYCODE.ESC && this._nav.opened) {
      this._handleMenuClosed();
      return;
    }
  }

  _handleScroll(evt) {
    if (!this._scrolling) {
      this._scrolling = true;
      'requestAnimationFrame' in window
        ? requestAnimationFrame(this._autoHide)
        : setTimeout(this._autoHide, 50);
    }
  }

  _autoHide() {
    // If not desktop then exit
    if (!window.matchMedia(`(min-width: ${BREAKPOINT.SM}px)`).matches) {
      this._scrolling = false;
      return;
    }

    // If menu is open, return
    if (this._nav.expanded) {
      return;
    }

    const currentPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    if (currentPosition > 10 && currentPosition > this._lastPosition) {
      document.body.classList.add('header-is-fixed');
    }

    if (currentPosition < this._lastPosition - 20) {
      document.body.classList.remove('header-is-fixed');
      this._lastPosition = currentPosition;
    }

    // Don't set last position when scrolling back up
    if (!(currentPosition < this._lastPosition)) {
      this._lastPosition = currentPosition;
    }

    this._scrolling = false;
  }
}

customElements.define(WPHeader.is, WPHeader);
